"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, CheckCircle2, Award, Sparkles } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { useTranslations } from "next-intl";
import Link from "next/link";

export function Hero() {
  const t = useTranslations("HomePage.Hero");

  // Mouse Parallax State
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Scroll Count-Up State
  const statsRef = useRef<HTMLDivElement>(null);
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const [counts, setCounts] = useState({ tools: 0, boards: 0, accuracy: 0 });

  // Mouse Parallax Handler (Desktop)
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dashboardRef.current) return;
    const rect = dashboardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    // Rotate max 4 deg
    const tiltX = -(y / (rect.height / 2)) * 4;
    const tiltY = (x / (rect.width / 2)) * 4;
    setTilt({ x: tiltX, y: tiltY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  // IntersectionObserver for Count-Up Stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasScrolledIntoView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate count-up stats
  useEffect(() => {
    if (!hasScrolledIntoView) return;

    let start = 0;
    const duration = 1200; // ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3); // Ease-out cubic

      setCounts({
        tools: Math.floor(easeOut * 11),
        boards: Math.floor(easeOut * 3),
        accuracy: Math.floor(easeOut * 100),
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasScrolledIntoView]);

  const headingWords = t("heading").split(" ");

  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-background/80 to-muted/30 pt-24 pb-16 md:pt-32 md:pb-24">
      {/* Organic Background Blobs */}
      <div className="absolute top-1/4 -left-20 h-96 w-96 rounded-full bg-[#FF6B3D]/15 blur-3xl animate-blob-float-1 pointer-events-none" />
      <div className="absolute top-1/3 -right-20 h-96 w-96 rounded-full bg-[#4B3FD1]/15 blur-3xl animate-blob-float-2 pointer-events-none" />

      {/* Navigation */}
      <SiteHeader />

      {/* Hero Main Content */}
      <div className="relative px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-center text-center">
            {/* Eyebrow Label */}
            <p className="mb-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
              {t("badge")}
            </p>

            {/* Kinetic Headline */}
            <h1 className="mb-6 max-w-4xl text-balance text-4xl font-extrabold leading-tight tracking-tight text-foreground sm:text-6xl md:text-7xl">
              {headingWords.map((word, i) => (
                <span
                  key={i}
                  className="inline-block transition-all duration-700 ease-out"
                  style={{
                    animation: `wordRise 0.6s ease-out ${i * 60}ms both`,
                  }}
                >
                  {word}&nbsp;
                </span>
              ))}
              <span className="inline-block bg-gradient-to-r from-[#FF6B3D] via-[#14B8A6] to-[#4B3FD1] bg-clip-text text-transparent animate-gradient-loop font-black">
                {t("headingHighlight")}
              </span>
            </h1>

            {/* Subheading */}
            <p className="mb-10 max-w-2xl text-balance text-base text-muted-foreground sm:text-lg md:text-xl leading-relaxed">
              {t("subheading")}
            </p>

            {/* CTA Button */}
            <div className="flex flex-col gap-4 sm:flex-row items-center">
              <Link href="/login">
                <Button
                  size="lg"
                  className="group relative h-12 px-8 text-base font-bold bg-[#FF6B3D] text-white hover:bg-[#FF6B3D]/90 animate-breathing-button hover:scale-105 transition-transform"
                >
                  <span>{t("ctaPrimary")}</span>
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            {/* Live Count-Up Stats */}
            <div
              ref={statsRef}
              className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 md:gap-16 border-t border-border/60 pt-10 w-full max-w-4xl"
            >
              <div className="flex flex-col items-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-[#FF6B3D]">
                  {counts.tools}+
                </div>
                <div className="mt-1 text-xs sm:text-sm font-medium text-muted-foreground">
                  AI Career Tools
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-[#14B8A6]">
                  {counts.boards}
                </div>
                <div className="mt-1 text-xs sm:text-sm font-medium text-muted-foreground">
                  Ethiopian Job Portals
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-[#4B3FD1]">
                  {counts.accuracy}%
                </div>
                <div className="mt-1 text-xs sm:text-sm font-medium text-muted-foreground">
                  Real-Time Feedback
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Animated Dashboard Visual (Interactive Composite) */}
        <div
          ref={dashboardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="relative mx-auto mt-16 max-w-5xl transition-transform duration-200 ease-out"
          style={{
            transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          }}
        >
          <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card p-6 shadow-2xl backdrop-blur-xl">
            {/* Dashboard Header Bar */}
            <div className="mb-6 flex items-center justify-between border-b border-border/60 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-rose-500/80" />
                  <div className="h-3 w-3 rounded-full bg-amber-500/80" />
                  <div className="h-3 w-3 rounded-full bg-emerald-500/80" />
                </div>
                <span className="text-xs font-semibold text-muted-foreground">
                  Career Loop AI Intelligence Suite
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  Live System Ready
                </span>
              </div>
            </div>

            {/* Dashboard Composite Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Card 1: Staggered Bar Chart */}
              <div className="rounded-xl border border-border/60 bg-muted/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-foreground">ATS Match Growth</span>
                  <TrendingUp className="h-4 w-4 text-[#FF6B3D]" />
                </div>
                <div className="flex h-32 items-end justify-between gap-2 pt-4">
                  {[45, 60, 75, 92].map((height, i) => (
                    <div key={i} className="flex flex-col items-center flex-1 h-full justify-end">
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-[#FF6B3D] to-[#4B3FD1] transition-all duration-1000 ease-out"
                        style={{
                          height: hasScrolledIntoView ? `${height}%` : "0%",
                          transitionDelay: `${i * 150}ms`,
                        }}
                      />
                      <span className="mt-1 text-[10px] text-muted-foreground">
                        W{i + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card 2: Donut Chart Arc */}
              <div className="rounded-xl border border-border/60 bg-muted/40 p-4 flex flex-col items-center justify-center">
                <span className="text-xs font-bold text-foreground mb-2">
                  Skill Match Ratio
                </span>
                <div className="relative flex items-center justify-center">
                  <svg className="h-28 w-28 transform -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="44"
                      className="stroke-muted stroke-[10] fill-none"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="44"
                      className="stroke-[#14B8A6] stroke-[10] fill-none transition-all duration-1500 ease-out"
                      strokeDasharray="276"
                      strokeDashoffset={hasScrolledIntoView ? "30" : "276"}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center">
                    <span className="text-xl font-extrabold text-foreground">88%</span>
                    <span className="text-[10px] text-muted-foreground">Matched</span>
                  </div>
                </div>
              </div>

              {/* Card 3: Line Chart Drawing */}
              <div className="rounded-xl border border-border/60 bg-muted/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-foreground">Interview Confidence</span>
                  <Sparkles className="h-4 w-4 text-[#14B8A6]" />
                </div>
                <div className="relative h-28 w-full flex items-center">
                  <svg className="h-full w-full overflow-visible">
                    <path
                      d="M 10 70 Q 60 20, 120 50 T 230 15"
                      fill="none"
                      stroke="#4B3FD1"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className="transition-all duration-1500 ease-out"
                      strokeDasharray="300"
                      strokeDashoffset={hasScrolledIntoView ? "0" : "300"}
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Floating Activity Toast Cards */}
            <div className="absolute -left-4 top-1/4 hidden md:flex items-center gap-3 rounded-xl border border-border/80 bg-background/95 p-3.5 shadow-2xl backdrop-blur-md animate-bounce-slow">
              <div className="rounded-lg bg-[#FF6B3D]/15 p-2 text-[#FF6B3D]">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-foreground">+45% Match Score</div>
                <div className="text-[10px] text-muted-foreground">AI Job Matcher</div>
              </div>
            </div>

            <div className="absolute -right-4 top-1/3 hidden md:flex items-center gap-3 rounded-xl border border-border/80 bg-background/95 p-3.5 shadow-2xl backdrop-blur-md">
              <div className="rounded-lg bg-[#14B8A6]/15 p-2 text-[#14B8A6]">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-foreground">Mock Interview Ready</div>
                <div className="text-[10px] text-muted-foreground">Clarity Score: 92%</div>
              </div>
            </div>

            <div className="absolute left-1/3 -bottom-5 hidden md:flex items-center gap-3 rounded-xl border border-border/80 bg-background/95 p-3 shadow-2xl backdrop-blur-md">
              <div className="rounded-lg bg-[#4B3FD1]/15 p-2 text-[#4B3FD1]">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-foreground">ATS Resume Verified</div>
                <div className="text-[10px] text-muted-foreground">Tailored for Target Role</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
